import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, Scatter
} from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import {
  BarChart3, PieChart as PieChartIcon, TrendingUp, FileText, Users, Clock,
  CheckCircle, AlertTriangle, Calendar, Activity, Shield, ArrowLeft,
  Download, RefreshCw, Filter, Layers, Target, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emergent-style color palette
const COLORS = {
  primary: '#3B82F6',     // Blue
  secondary: '#8B5CF6',   // Purple
  accent: '#06B6D4',      // Cyan
  success: '#10B981',     // Green
  warning: '#F59E0B',     // Amber
  danger: '#EF4444',      // Red
  info: '#6366F1',        // Indigo
  muted: '#64748B',       // Slate
};

const CHART_COLORS = [
  '#3B82F6', '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#6366F1'
];

// Status colors for pie chart
const STATUS_COLORS = {
  draft: '#64748B',
  pending_review: '#F59E0B',
  in_review: '#3B82F6',
  approved: '#10B981',
  rejected: '#EF4444',
  archived: '#8B5CF6',
  superseded: '#6B7280'
};

// Custom tooltip component - defined outside to avoid re-creation on each render
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-50/95 backdrop-blur-sm border border-slate-300 rounded-lg p-3 shadow-xl">
        <p className="text-slate-700 text-sm font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-bold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Legend formatter - defined outside to avoid re-creation
const legendFormatter = (value) => <span className="text-slate-700 text-xs">{value}</span>;

export default function AnalyticsDashboardPage() {
  const { user, getAuthHeaders, isAdmin, selectedStudy: contextStudy } = useAuth();
  const navigate = useNavigate();

  // State - Use context study as default
  const [loading, setLoading] = useState(true);
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(contextStudy?.study_id || 'all');
  const [timeRange, setTimeRange] = useState('30');
  const [stats, setStats] = useState(null);
  const [uploadTrend, setUploadTrend] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);
  const [folderDistribution, setFolderDistribution] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [complianceMetrics, setComplianceMetrics] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  
  // New: Personal vs Team metrics toggle
  const [metricsMode, setMetricsMode] = useState('personal'); // 'personal' or 'team'
  const [personalStats, setPersonalStats] = useState({ uploaded: 0, approved: 0, pending: 0 });
  const [teamStats, setTeamStats] = useState({ uploaded: 0, approved: 0, pending: 0 });
  const [myStudiesCount, setMyStudiesCount] = useState(0);
  
  // Predictive Analytics State
  const [predictiveData, setPredictiveData] = useState(null);
  const [loadingPredictive, setLoadingPredictive] = useState(false);

  // Set selected study from context when it changes
  useEffect(() => {
    if (contextStudy?.study_id) {
      setSelectedStudy(contextStudy.study_id);
    }
  }, [contextStudy]);

  // Fetch all analytics data
  useEffect(() => {
    // Only fetch if user is authenticated
    if (user) {
      fetchAnalyticsData();
      fetchPredictiveAnalytics();
    }
  }, [user, selectedStudy, timeRange]);

  const fetchPredictiveAnalytics = async () => {
    setLoadingPredictive(true);
    try {
      const studyParam = selectedStudy !== 'all' ? `?study_id=${selectedStudy}` : '';
      const response = await axios.get(`${API_URL}/api/analytics/predictive${studyParam}`, getAuthHeaders());
      setPredictiveData(response.data);
    } catch (error) {
      console.error('Error fetching predictive analytics:', error);
    } finally {
      setLoadingPredictive(false);
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Fetch studies
      const studiesRes = await axios.get(`${API_URL}/api/studies`, getAuthHeaders());
      setStudies(studiesRes.data);
      setMyStudiesCount(studiesRes.data.length);

      // Fetch dashboard stats (admin only)
      if (isAdmin()) {
        try {
          const statsRes = await axios.get(`${API_URL}/api/admin/dashboard/stats`, getAuthHeaders());
          setStats(statsRes.data);
        } catch (e) {
          console.log('Stats not available for non-admin');
        }
      }

      // Fetch documents for analysis
      let documents = [];
      if (selectedStudy === 'all') {
        // Fetch from all studies
        for (const study of studiesRes.data) {
          try {
            const docsRes = await axios.get(`${API_URL}/api/studies/${study.id}/all-documents`, getAuthHeaders());
            documents = [...documents, ...(docsRes.data || [])];
          } catch (e) {
            console.log(`Could not fetch docs for study ${study.id}`);
          }
        }
      } else {
        try {
          const docsRes = await axios.get(`${API_URL}/api/studies/${selectedStudy}/all-documents`, getAuthHeaders());
          documents = docsRes.data || [];
        } catch (e) {
          console.log('Could not fetch documents for selected study');
        }
      }

      // Calculate Personal vs Team Stats
      const userEmail = user?.email?.toLowerCase();
      const personalDocs = documents.filter(doc => 
        doc.uploaded_by?.toLowerCase() === userEmail || 
        doc.created_by?.toLowerCase() === userEmail
      );
      const teamDocs = documents;

      setPersonalStats({
        uploaded: personalDocs.length,
        approved: personalDocs.filter(d => d.status === 'approved').length,
        pending: personalDocs.filter(d => ['pending_review', 'in_review', 'draft'].includes(d.status)).length
      });

      setTeamStats({
        uploaded: teamDocs.length,
        approved: teamDocs.filter(d => d.status === 'approved').length,
        pending: teamDocs.filter(d => ['pending_review', 'in_review', 'draft'].includes(d.status)).length
      });

      // Process analytics data
      processUploadTrend(documents);
      processStatusDistribution(documents);
      processFolderDistribution(documents);
      processDocumentTypes(documents);
      processComplianceMetrics(documents);

      // Fetch user activity if admin
      if (isAdmin()) {
        try {
          const activityRes = await axios.get(
            `${API_URL}/api/admin/dashboard/activity/paginated?limit=100`,
            getAuthHeaders()
          );
          processUserActivity(activityRes.data.items || []);
        } catch (e) {
          console.log('Activity data not available');
        }
      }

    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Don't show error toast - gracefully degrade with empty data
    } finally {
      setLoading(false);
    }
  };

  const processUploadTrend = (documents) => {
    const days = parseInt(timeRange);
    const trend = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const count = documents.filter(doc => 
        doc.created_at && doc.created_at.startsWith(dateStr)
      ).length;
      
      trend.push({
        date: format(date, 'MMM dd'),
        fullDate: dateStr,
        uploads: count,
        cumulative: 0 // Will be calculated below
      });
    }

    // Calculate cumulative
    let cumulative = 0;
    trend.forEach(item => {
      cumulative += item.uploads;
      item.cumulative = cumulative;
    });

    setUploadTrend(trend);
  };

  const processStatusDistribution = (documents) => {
    const statusCounts = {};
    documents.forEach(doc => {
      const status = doc.status || 'draft';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const distribution = Object.entries(statusCounts).map(([status, count]) => ({
      name: status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: count,
      status: status,
      color: STATUS_COLORS[status] || '#6B7280'
    }));

    setStatusDistribution(distribution);
  };

  const processFolderDistribution = (documents) => {
    const folderCounts = {};
    documents.forEach(doc => {
      const folder = doc.artifact_classification || 'Unclassified';
      const mainFolder = folder.split('/')[0] || folder;
      folderCounts[mainFolder] = (folderCounts[mainFolder] || 0) + 1;
    });

    const distribution = Object.entries(folderCounts)
      .map(([name, value]) => ({ name: name.substring(0, 20), fullName: name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    setFolderDistribution(distribution);
  };

  const processDocumentTypes = (documents) => {
    const typeCounts = {};
    documents.forEach(doc => {
      const type = doc.document_type || 'Unknown';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    const types = Object.entries(typeCounts)
      .map(([name, value]) => ({ name: name.substring(0, 25), fullName: name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    setDocumentTypes(types);
  };

  const processUserActivity = (activities) => {
    const actionCounts = {};
    activities.forEach(activity => {
      const action = activity.action || 'unknown';
      actionCounts[action] = (actionCounts[action] || 0) + 1;
    });

    const activityData = Object.entries(actionCounts)
      .map(([name, value]) => ({ name: name.replace('_', ' '), value }))
      .sort((a, b) => b.value - a.value);

    setUserActivity(activityData);
  };

  const processComplianceMetrics = (documents) => {
    // ALCOA+ compliance metrics
    const totalDocs = documents.length || 1;
    
    const metrics = [
      {
        subject: 'Attributable',
        value: documents.filter(d => d.uploaded_by).length / totalDocs * 100,
        fullMark: 100,
        description: 'Documents with clear ownership'
      },
      {
        subject: 'Legible',
        value: documents.filter(d => d.name && d.file_type).length / totalDocs * 100,
        fullMark: 100,
        description: 'Documents with proper naming'
      },
      {
        subject: 'Contemporaneous',
        value: documents.filter(d => d.created_at && d.document_date).length / totalDocs * 100,
        fullMark: 100,
        description: 'Documents with timestamps'
      },
      {
        subject: 'Original',
        value: documents.filter(d => d.version === 1 || d.version).length / totalDocs * 100,
        fullMark: 100,
        description: 'Original/versioned documents'
      },
      {
        subject: 'Accurate',
        value: documents.filter(d => d.document_type && d.metadata).length / totalDocs * 100,
        fullMark: 100,
        description: 'Documents with complete metadata'
      },
      {
        subject: 'Complete',
        value: documents.filter(d => d.status !== 'draft').length / totalDocs * 100,
        fullMark: 100,
        description: 'Documents past draft stage'
      }
    ];

    setComplianceMetrics(metrics);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 w-full max-w-full overflow-x-hidden">
      {/* Header - Professional Slate Blue */}
      <header className="relative z-10 border-b border-neutral-200 bg-[#1E3A5F] shadow-sm w-full">
        <div className="w-full max-w-full px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-5 w-px bg-white/20 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#2563EB] flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">Analytics Dashboard</h1>
                  <p className="text-[10px] text-white/60 hidden sm:block">Clinical Document Intelligence</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Study Context */}
              {contextStudy?.study_id && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-white/10 border border-white/20">
                  <span className="text-sm font-medium text-white truncate max-w-[150px]">{contextStudy.study_name}</span>
                </div>
              )}

              {/* Time Range */}
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-28 bg-white/10 border-white/20 text-white text-sm h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-neutral-200">
                  <SelectItem value="7" className="text-neutral-700">7 days</SelectItem>
                  <SelectItem value="14" className="text-neutral-700">14 days</SelectItem>
                  <SelectItem value="30" className="text-neutral-700">30 days</SelectItem>
                  <SelectItem value="90" className="text-neutral-700">90 days</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="sm"
                onClick={fetchAnalyticsData}
                className="text-white/80 hover:text-white hover:bg-white/10 h-8"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-full px-4 sm:px-6 py-4 space-y-4">
        {/* Metrics Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-neutral-200 shadow-sm">
            <Button
              size="sm"
              variant={metricsMode === 'personal' ? 'default' : 'ghost'}
              onClick={() => setMetricsMode('personal')}
              className={metricsMode === 'personal' 
                ? 'bg-[#1E3A5F] text-white h-7 text-xs' 
                : 'text-neutral-600 hover:text-neutral-900 h-7 text-xs'}
            >
              My Documents
            </Button>
            <Button
              size="sm"
              variant={metricsMode === 'team' ? 'default' : 'ghost'}
              onClick={() => setMetricsMode('team')}
              className={metricsMode === 'team' 
                ? 'bg-[#1E3A5F] text-white h-7 text-xs' 
                : 'text-neutral-600 hover:text-neutral-900 h-7 text-xs'}
            >
              Team Documents
            </Button>
          </div>
          <p className="text-xs text-neutral-500">
            {metricsMode === 'personal' ? 'Showing documents you uploaded' : 'Showing all team documents'}
          </p>
        </div>

        {/* KPI Cards - Updated with personal/team toggle */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Your Uploads / Total Documents */}
          <Card className="bg-white border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-500 text-xs font-medium uppercase tracking-wide">
                    {metricsMode === 'personal' ? 'Your Uploads' : 'Total Documents'}
                  </p>
                  <p className="text-2xl font-bold text-neutral-900 mt-1">
                    {metricsMode === 'personal' ? personalStats.uploaded : teamStats.uploaded}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#1E3A5F]" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-[#2563EB] font-medium">
                <TrendingUp className="w-3 h-3 mr-1" />
                <span>+{uploadTrend.slice(-7).reduce((a, b) => a + b.uploads, 0)} this week</span>
              </div>
            </CardContent>
          </Card>

          {/* Approved */}
          <Card className="bg-white border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-500 text-xs font-medium uppercase tracking-wide">Approved</p>
                  <p className="text-2xl font-bold text-neutral-900 mt-1">
                    {metricsMode === 'personal' ? personalStats.approved : teamStats.approved}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <div className="mt-2">
                <div className="h-1 bg-neutral-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ 
                      width: `${((metricsMode === 'personal' ? personalStats.approved : teamStats.approved) / 
                        Math.max((metricsMode === 'personal' ? personalStats.uploaded : teamStats.uploaded), 1)) * 100}%` 
                    }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Review */}
          <Card className="bg-white border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-500 text-xs font-medium uppercase tracking-wide">Pending Review</p>
                  <p className="text-2xl font-bold text-neutral-900 mt-1">
                    {metricsMode === 'personal' ? personalStats.pending : teamStats.pending}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-amber-600">
                <AlertTriangle className="w-3 h-3 mr-1" />
                <span>Requires attention</span>
              </div>
            </CardContent>
          </Card>

          {/* My Studies */}
          <Card className="bg-white border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-500 text-xs font-medium uppercase tracking-wide">My Studies</p>
                  <p className="text-2xl font-bold text-neutral-900 mt-1">{myStudiesCount}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-[#2563EB]" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-neutral-500">
                <Activity className="w-3 h-3 mr-1" />
                <span>Studies with access</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Upload Trend - Area Chart */}
          <Card className="lg:col-span-2 bg-white border-neutral-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-neutral-900 text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#2563EB]" />
                    Document Upload Trend
                  </CardTitle>
                  <CardDescription className="text-neutral-500 text-xs">Daily uploads over time</CardDescription>
                </div>
                <Badge className="bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20">
                  {uploadTrend.reduce((a, b) => a + b.uploads, 0)} total
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={uploadTrend}>
                  <defs>
                    <linearGradient id="uploadGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="uploads" 
                    stroke="#2563EB" 
                    strokeWidth={2}
                    fill="url(#uploadGradient)" 
                    name="Uploads"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Status Distribution - Pie Chart */}
          <Card className="bg-white border-neutral-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-neutral-900 text-base flex items-center gap-2">
                <PieChartIcon className="w-4 h-4 text-[#1E3A5F]" />
                Status Distribution
              </CardTitle>
              <CardDescription className="text-neutral-500 text-xs">Document workflow status</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    formatter={legendFormatter}
                    wrapperStyle={{ paddingTop: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-2 gap-6">
          {/* Document Types - Bar Chart */}
          <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-400" />
                Document Types
              </CardTitle>
              <CardDescription className="text-slate-600">Distribution by document type</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={documentTypes} layout="vertical">
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#06B6D4" />
                      <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={10} width={150} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="url(#barGradient)" radius={[0, 4, 4, 0]} name="Count" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* ALCOA+ Compliance - Radar Chart */}
          <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    ALCOA+ Compliance Score
                  </CardTitle>
                  <CardDescription className="text-slate-600">Data integrity metrics</CardDescription>
                </div>
                <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                  {Math.round(complianceMetrics.reduce((a, b) => a + b.value, 0) / Math.max(complianceMetrics.length, 1))}% avg
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={complianceMetrics}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="subject" stroke="#94a3b8" fontSize={11} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#94a3b8" fontSize={10} />
                  <Radar
                    name="Compliance"
                    dataKey="value"
                    stroke="#10B981"
                    fill="#10B981"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 3 */}
        <div className="grid grid-cols-2 gap-6">
          {/* Folder Distribution */}
          <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-400" />
                TMF Section Distribution
              </CardTitle>
              <CardDescription className="text-slate-600">Documents by folder/section</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={folderDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} angle={-45} textAnchor="end" height={80} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Documents" radius={[4, 4, 0, 0]}>
                    {folderDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* User Activity */}
          {isAdmin() && userActivity.length > 0 && (
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-pink-400" />
                  User Activity
                </CardTitle>
                <CardDescription className="text-slate-600">Actions performed</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={userActivity}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {userActivity.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Compliance Summary for non-admins */}
          {!isAdmin() && (
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-400" />
                  Compliance Summary
                </CardTitle>
                <CardDescription className="text-slate-600">Regulatory readiness</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                {complianceMetrics.map((metric, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">{metric.subject}</span>
                      <span className="text-slate-900 font-medium">{Math.round(metric.value)}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${metric.value}%`,
                          background: metric.value >= 80 ? 'linear-gradient(to right, #10B981, #34D399)' :
                                     metric.value >= 50 ? 'linear-gradient(to right, #F59E0B, #FBBF24)' :
                                     'linear-gradient(to right, #EF4444, #F87171)'
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Predictive Analytics Section */}
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Predictive Analytics</h2>
              <p className="text-sm text-slate-600">AI-powered insights and forecasts</p>
            </div>
            {loadingPredictive && (
              <RefreshCw className="w-5 h-5 text-purple-600 animate-spin ml-2" />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Document Completion Forecasts */}
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-red-600" />
                  Document Completion Forecasts
                </CardTitle>
                <CardDescription className="text-slate-600">Predicted completion dates for pending documents</CardDescription>
              </CardHeader>
              <CardContent>
                {predictiveData?.document_completion_forecast?.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {predictiveData.document_completion_forecast.map((item, index) => (
                      <div key={index} className="p-3 bg-slate-100/80 rounded-lg border border-slate-300/80">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-900 truncate max-w-[200px]">{item.document_name}</span>
                          <Badge variant="outline" className={`text-xs ${
                            item.days_remaining <= 1 ? 'border-green-500 text-green-600' :
                            item.days_remaining <= 3 ? 'border-amber-500 text-amber-600' :
                            'border-slate-400 text-slate-600'
                          }`}>
                            {item.days_remaining} days
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-600">
                          <span>Status: {item.status?.replace(/_/g, ' ')}</span>
                          <span>Confidence: {Math.round(item.confidence * 100)}%</span>
                        </div>
                        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-red-500 to-cyan-400 rounded-full"
                            style={{ width: `${Math.min(100, (1 - item.days_remaining / 7) * 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[200px] text-slate-600">
                    <Clock className="w-8 h-8 mb-2 opacity-50" />
                    <p>No pending documents to forecast</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compliance Risk Scores */}
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Compliance Risk Scores
                </CardTitle>
                <CardDescription className="text-slate-600">Documents with potential compliance issues</CardDescription>
              </CardHeader>
              <CardContent>
                {predictiveData?.compliance_risk_scores?.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {predictiveData.compliance_risk_scores.slice(0, 8).map((item, index) => (
                      <div key={index} className="p-3 bg-slate-100/80 rounded-lg border border-slate-300/80">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-900 truncate max-w-[180px]">{item.document_name}</span>
                          <Badge className={`text-xs ${
                            item.risk_level === 'High' ? 'bg-red-500/20 text-red-600 border-red-500/30' :
                            item.risk_level === 'Medium' ? 'bg-amber-500/20 text-amber-600 border-amber-500/30' :
                            'bg-green-500/20 text-green-600 border-green-500/30'
                          }`}>
                            {item.risk_level} ({item.risk_score})
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.risk_factors?.slice(0, 3).map((factor, fidx) => (
                            <span key={fidx} className="text-xs px-2 py-0.5 bg-slate-200/50 rounded text-slate-700">
                              {factor}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[200px] text-slate-600">
                    <CheckCircle className="w-8 h-8 mb-2 text-green-600 opacity-50" />
                    <p>No compliance risks detected</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Workflow Bottlenecks */}
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Workflow Bottleneck Analysis
                </CardTitle>
                <CardDescription className="text-slate-600">Reviewers with high workload</CardDescription>
              </CardHeader>
              <CardContent>
                {predictiveData?.workflow_bottlenecks?.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {predictiveData.workflow_bottlenecks.map((item, index) => (
                      <div key={index} className="p-3 bg-slate-100/80 rounded-lg border border-slate-300/80">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-900">{item.reviewer_email}</span>
                          <Badge variant="outline" className={`text-xs ${
                            item.bottleneck_risk > 50 ? 'border-red-500 text-red-600' :
                            item.bottleneck_risk > 25 ? 'border-amber-500 text-amber-600' :
                            'border-green-500 text-green-600'
                          }`}>
                            {item.bottleneck_risk}% risk
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                          <div className="text-center p-2 bg-slate-200/50 rounded">
                            <div className="text-slate-900 font-medium">{item.assigned_count}</div>
                            <div className="text-slate-600">Assigned</div>
                          </div>
                          <div className="text-center p-2 bg-slate-200/50 rounded">
                            <div className="text-slate-900 font-medium">{item.completed_count}</div>
                            <div className="text-slate-600">Completed</div>
                          </div>
                          <div className="text-center p-2 bg-slate-200/50 rounded">
                            <div className="text-slate-900 font-medium">{item.current_pending}</div>
                            <div className="text-slate-600">Pending</div>
                          </div>
                        </div>
                        {item.recommendation && (
                          <p className="text-xs text-purple-600 mt-2 italic">{item.recommendation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[200px] text-slate-600">
                    <Users className="w-8 h-8 mb-2 opacity-50" />
                    <p>No bottlenecks detected</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upload Trend Forecasting */}
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  Upload Trend Forecast
                </CardTitle>
                <CardDescription className="text-slate-600">Historical and predicted upload volumes</CardDescription>
              </CardHeader>
              <CardContent>
                {predictiveData?.upload_trend_forecast?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={predictiveData.upload_trend_forecast}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#94a3b8" 
                        tick={{ fontSize: 10 }}
                        tickFormatter={(val) => val.slice(5)}
                      />
                      <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend formatter={legendFormatter} />
                      <Bar 
                        dataKey="actual_uploads" 
                        name="Actual" 
                        fill="#3B82F6" 
                        radius={[4, 4, 0, 0]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="predicted_uploads" 
                        name="Predicted" 
                        stroke="#06B6D4" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ fill: '#06B6D4', r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[200px] text-slate-600">
                    <TrendingUp className="w-8 h-8 mb-2 opacity-50" />
                    <p>Insufficient data for forecasting</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Regulatory Compliance Footer */}
        <Card className="bg-[#1E3A5F] border-[#1E3A5F] shadow-sm">
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-medium">Compliant with:</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Badge className="bg-white/10 text-white border-white/20 text-xs">21 CFR Part 11</Badge>
                  <Badge className="bg-white/10 text-white border-white/20 text-xs">ICH-GCP E6</Badge>
                  <Badge className="bg-white/10 text-white border-white/20 text-xs">EU Annex 11</Badge>
                  <Badge className="bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30 text-xs">ALCOA+</Badge>
                  <Badge className="bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30 text-xs">FDA Ready</Badge>
                </div>
              </div>
              <div className="text-xs text-white/60">
                eTMF Vault •  A Product of <a href="https://www.vetforcesolutions.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Vetforce Solutions</a>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
